/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : detail component
 *   Integrations : goal/view-detail, goal/upsert
 *   Version : 2.0.0
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useParams, useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../Components/Buttons";
import { Input, CustomFileUpload } from "../../Components/FormElements";
import { APIService } from "../../Service";
import { EmptyScreen, Loader } from "../../Components/Other";
import { toast } from "react-toastify";

export default function GoalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  // Form states
  const [goalName, setGoalName] = useState("");
  const [goalNameError, setGoalNameError] = useState("");

  const [about, setAbout] = useState("");
  const [aboutError, setAboutError] = useState("");

  const [minSIP, setMinSIP] = useState("");
  const [minSIPErr, setMinSIPErr] = useState("");

  const [minLumpsum, setMinLumpsum] = useState("");
  const [minLumpsumErr, setMinLumpsumErr] = useState("");

  // logo upload
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");

  // Display-only stats
  const [goalData, setGoalData] = useState(null);
  const [createdOn, setCreatedOn] = useState("");
  const [customersInvested, setCustomersInvested] = useState("0");
  const [threeYearReturns, setThreeYearReturns] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [returnYear, setReturnYear] = useState("");

  useEffect(() => {
    _getGoalDetail();
  }, [id]);

  useEffect(() => {
    if (goalData) {
      dispatch(setPageTitle(["Manage Goal", goalData.name || "Goal Detail"]));
    }
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-goal"]));
  }, [id, goalData, dispatch]);

  // service to get goal detail
  const _getGoalDetail = () => {
    setLoader(true);
    const payload = [{ goal_style_id: id }];

    APIService(true, "goal/view-detail", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data && res.data.length > 0) {
          const data = res.data[0];
          setGoalData(data);

          // Form values
          setGoalName(data.name || "");
          setAbout(data.description || "");
          setMinSIP(data.minimum_sip?.toString() || "");
          setMinLumpsum(data.minimum_lumpsum?.toString() || "");
          setLogoUrl(data.image || "");

          // Display stats
          setCreatedOn(data.created_on || "");
          setLastUpdated(data.updated_on || "");
          setCustomersInvested(data.invested_users_count || "0");
          setThreeYearReturns(data.return_percentage || "");
          setReturnYear(data.return_year || "3Y");

          setIsEmpty(false);
        } else {
          setIsEmpty(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching goal details:", error);
        setIsEmpty(true);
      })
      .finally(() => setLoader(false));
  };

  // form validation
  const validateForm = () => {
    let valid = true;

    if (!goalName.trim()) {
      setGoalNameError("Goal name is required");
      valid = false;
    } else if (goalName.trim().length < 3) {
      setGoalNameError("Goal name must be at least 3 characters");
      valid = false;
    }

    if (!about.trim()) {
      setAboutError("About is required");
      valid = false;
    } else if (about.trim().length < 3) {
      setAboutError("About must be at least 10 characters");
      valid = false;
    }

    if (!minSIP.trim()) {
      setMinSIPErr("Min SIP is required");
      valid = false;
    } else if (isNaN(minSIP) || parseInt(minSIP) <= 0) {
      setMinSIPErr("Min SIP must be a valid positive number");
      valid = false;
    } else if (parseInt(minSIP) < 100) {
      setMinSIPErr("Min SIP must be at least ₹100");
      valid = false;
    }

    if (!minLumpsum.trim()) {
      setMinLumpsumErr("Min Lumpsum is required");
      valid = false;
    } else if (isNaN(minLumpsum) || parseInt(minLumpsum) <= 0) {
      setMinLumpsumErr("Min Lumpsum must be a valid positive number");
      valid = false;
    } else if (parseInt(minLumpsum) < 500) {
      setMinLumpsumErr("Min Lumpsum must be at least ₹500");
      valid = false;
    } else if (parseInt(minLumpsum) < parseInt(minSIP)) {
      setMinLumpsumErr(
        "Min Lumpsum should be greater than or equal to Min SIP",
      );
      valid = false;
    }

    if (!logoFile && !logoUrl) {
      setLogoError("Logo is required");
      valid = false;
    }

    return valid;
  };

  const handleCancel = () => {
    navigate("/manage-goal");
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setLoader(true);

    const payload = [
      {
        goal_style_id: parseInt(id),
        name: goalName.trim(),
        description: about.trim(),
        image: logoUrl,
        min_sip: parseInt(minSIP),
        min_lumpsum: parseInt(minLumpsum),
      },
    ];

    APIService(true, "goal/upsert", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message);
          navigate("/manage-goal");
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
        console.error("Error updating goal:", error);
      })
      .finally(() => setLoader(false));
  };

  return loader ? (
    <Loader />
  ) : isEmpty ? (
    <EmptyScreen message={"Goal detail not found"} />
  ) : (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card bg-transparent rounded-24 p-24 d-flex flex-column row-gap-3">
          {/* TOP CARD */}
          <div className="row">
            <div className="col-12">
              <div className="d-flex w-100 flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 rg-24 p-24">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center text-eerie-black">
                    <div className="e-w-fit rounded-8 bg-white e-border border-facebook-blue-4 border-opacity-10 p-1">
                      <img
                        src={
                          logoUrl || require("../../Assets/Images/avatar.jpg")
                        }
                        alt="goal image"
                        width={58}
                      />
                    </div>
                    <h5 className="fs-18 ps-19 lh-20 fw-500 mb-0 text-capitalize">
                      {goalName}
                    </h5>
                  </div>

                  <div className="d-flex align-items-center justify-content-end">
                    <PrimaryButton
                      type={2}
                      icon="chevron-right"
                      ClassName="bg-transparent p-0 fs-14 fw-400 lh-20 text-facebook-blue"
                      size="12px"
                      onPress={() =>
                        navigate(`/manage-goal/detail/${id}/rebalance-history`)
                      }
                      name={"View Rebalance History"}
                    />
                  </div>
                </div>

                {/* STATS */}
                <div className="row row-cols-lg-5 row-cols-md-4 row-cols-sm-3 row-cols-2 rg-32">
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">Created On</h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0">
                      {createdOn}
                    </p>
                  </div>
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Customers Invested
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      {customersInvested}
                    </p>
                  </div>
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">Min SIP</h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      ₹{minSIP}
                    </p>
                  </div>
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Min Lumpsum
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0 text-truncate">
                      ₹{minLumpsum}
                    </p>
                  </div>
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      {returnYear} Returns
                    </h4>
                    <p className="fw-400 fs-18 text-medium-sea-green m-0 text-truncate">
                      {threeYearReturns}
                    </p>
                  </div>
                  <div className="col">
                    <h4 className="fs-14 fw-400 text-davys-gray">
                      Last Updated
                    </h4>
                    <p className="fw-400 fs-18 text-eerie-black m-0">
                      {lastUpdated}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* UPDATE FORM */}
          <div className="row">
            <div className="col-12">
              <div className="d-flex flex-column e-glass-card bg-opacity-43 shadow-none rounded-16 row-gap-3 p-24">
                <div className="row">
                  <div className="col-lg-6 col-12">
                    <h4 className="fw-500 fs-18 text-eerie-black">
                      Update details
                    </h4>

                    <Input
                      id="goal_name"
                      type="text"
                      label="Goal Name"
                      placeholder="Enter Goal Name"
                      className="w-100 px-3 py-10"
                      require={true}
                      value={goalName}
                      error={goalNameError}
                      handleChange={(v) => {
                        setGoalName(v);
                        setGoalNameError("");
                      }}
                    />

                    <Input
                      id="about"
                      type="text"
                      label="About"
                      placeholder="Enter Goal About"
                      require={true}
                      value={about}
                      className="w-100 px-3 py-10"
                      error={aboutError}
                      handleChange={(v) => {
                        setAbout(v);
                        setAboutError("");
                      }}
                    />

                    <div className="d-flex flex-column row-gap-2 pb-2">
                      <p className="mb-0 fw-400 fs-14">
                        Upload logo <span className="text-cg-red">*</span>
                      </p>
                      <CustomFileUpload
                        file={logoFile || logoUrl}
                        type={2}
                        fileSize={3}
                        handleClear={() => {
                          setLogoFile(null);
                          setLogoUrl("");
                        }}
                        error={logoError}
                        handleChange={(file) => {
                          setLogoFile(file);
                          setLogoError("");
                        }}
                        onUploadComplete={(url) => {
                          setLogoUrl(url);
                          setLogoError("");
                        }}
                      />
                    </div>

                    <h4 className="fw-500 fs-18 text-eerie-black pt-24">
                      Min SIP & Lumpsum
                    </h4>

                    <div className="position-relative">
                      <Input
                        id="min_sip"
                        type="Number"
                        label="Min SIP"
                        placeholder="Enter Min SIP"
                        require={true}
                        value={minSIP}
                        error={minSIPErr}
                        className="w-100 ps-4 pe-3 py-10"
                        handleChange={(v) => {
                          const numValue = v.replace(/[^0-9]/g, "");
                          setMinSIP(numValue);
                          setMinSIPErr("");
                        }}
                      />
                      <span
                        style={{ top: "41%", left: "2%" }}
                        className="position-absolute"
                      >
                        ₹
                      </span>
                    </div>

                    <div className="position-relative">
                      <Input
                        id="min_lumpsum"
                        type="Number"
                        label="Min Lumpsum"
                        placeholder="Enter Min Lumpsum"
                        require={true}
                        className="w-100 ps-4 pe-3 py-10"
                        value={minLumpsum}
                        error={minLumpsumErr}
                        handleChange={(v) => {
                          const numValue = v.replace(/[^0-9]/g, "");
                          setMinLumpsum(numValue);
                          setMinLumpsumErr("");
                        }}
                      />
                      <span
                        style={{ top: "41%", left: "2%" }}
                        className="position-absolute"
                      >
                        ₹
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6 offset-lg-3 col-12">
                    <div className="row row-cols-2 pt-24">
                      <div className="col">
                        <PrimaryButton
                          name="Cancel"
                          ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                          onPress={handleCancel}
                        />
                      </div>
                      <div className="col">
                        <PrimaryButton
                          name="Submit"
                          ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                          onPress={handleSubmit}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
